import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { object, string } from 'yup';

import { sentenceCase } from '../../../../utils/utils';
import ActionButton from '../../../elements/action-button/ActionButton';
import Modal from '../../../sections/modal/Modal';

const BillingInfoModal = ({
  billingData,
  visible,
  handleClose,
  handleConfirm,
}) => {
  const { t } = useTranslation();

  const schema = object({
    payerCompany: string()
      .max(50, t('maxLength', { ns: 'validations', max: 50 }))
      .required(
        sentenceCase(
          t('inputRequired', {
            ns: 'validations',
            field: t('companyName', { ns: 'fields' }),
          })
        )
      ),
    payerSection: string().max(
      50,
      t('maxLength', { ns: 'validations', max: '50' })
    ),
    payerName: string()
      .max(50, t('maxLength', { ns: 'validations', max: '50' }))
      .required(
        sentenceCase(
          t('inputRequired', {
            ns: 'validations',
            field: t('contactName', { ns: 'fields' }),
          })
        )
      ),
    payerEmail: string()
      .email(
        sentenceCase(
          t('invalidFormat', {
            ns: 'validations',
            field: t('email', { ns: 'fields' }),
          })
        )
      )
      .max(256, t('maxLength', { ns: 'validations', max: '256' }))
      .required(
        sentenceCase(
          t('inputRequired', {
            ns: 'validations',
            field: t('email', { ns: 'fields' }),
          })
        )
      ),
    payerAddress: string()
      .max(512, t('maxLength', { ns: 'validations', max: '512' }))
      .required(
        sentenceCase(
          t('inputRequired', {
            ns: 'validations',
            field: t('address', { ns: 'fields' }),
          })
        )
      ),
  });

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  const handleFormSubmit = async (data) => {
    await handleConfirm(data);
    handleClose();
  };

  const formFields = [
    {
      name: 'payerCompany',
      defaultValue: billingData?.payerCompany,
      label: t('companyName', { ns: 'fields' }),
    },
    {
      name: 'payerSection',
      defaultValue: billingData?.payerSection,
      label: t('departmentName', { ns: 'fields' }),
    },
    {
      name: 'payerName',
      defaultValue: billingData?.payerName,
      label: t('contactName', { ns: 'fields' }),
    },
    {
      name: 'payerEmail',
      defaultValue: billingData?.payerEmail,
      label: t('email', { ns: 'fields' }),
    },
    {
      name: 'payerAddress',
      defaultValue: billingData?.payerAddress,
      label: t('address', { ns: 'fields' }),
    },
  ];

  return (
    <Modal
      openModal={visible}
      title={t('billingInfo', { ns: 'sectionTitles' })}
      handleClose={handleClose}
    >
      <form>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {formFields.map((formField) => (
            <Controller
              name={formField.name}
              control={control}
              defaultValue={billingData?.[formField.name] || ''}
              key={formField.name}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={formField.label}
                  error={!!errors?.[formField.name]}
                  helperText={errors[formField.name]?.message}
                  fullWidth
                  size="small"
                  inputProps={{ 'aria-label': formField.label }}
                />
              )}
            />
          ))}
        </Box>
      </form>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
        <Button variant="text" onClick={handleClose}>
          {t('cancel', { ns: 'buttons' })}
        </Button>
        <ActionButton clickAction={handleSubmit(handleFormSubmit)}>
          {t('confirm', { ns: 'buttons' })}
        </ActionButton>
      </Box>
    </Modal>
  );
};

BillingInfoModal.propTypes = {
  billingData: PropTypes.object,
  visible: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleConfirm: PropTypes.func.isRequired,
};

export default BillingInfoModal;
